import { useState, useRef, useEffect } from 'react';
import mapboxgl from 'mapbox-gl';
import mapPopUpTable from '../utils/mapPopUpTable';
import config from '../config';
import useAssetLayer from './useAssetLayer';
import addVectorTiles from '../utils/addVectorTiles';
import createLayerToggle from '../utils/createLayerToggle';

export default function useAreaMap({
  stateSchema,
  handleLocationIDsChange,
  operationalStates
}) {
  const { hybridLayers, hybridLayersLoaded } = useAssetLayer({
    stateSchema
  });

  mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;
  const areaMap = useRef(null);

  const [toggleableLayers, setToggleableLayers] = useState();
  const [mapStyle, setMapStyle] = useState('light-v10');

  const handleMapStyleChange = (newStyle) => setMapStyle(newStyle);

  useEffect(() => {
    if (!stateSchema || !hybridLayersLoaded) {
      const map = new mapboxgl.Map({
        container: areaMap.current,
        style: 'mapbox://styles/mapbox/' + mapStyle,
        attributionControl: false,
        // US Conus View
        bounds: config.conusBounds // [lon, lat] corners [LL, UR]
      });

      map.addControl(new mapboxgl.FullscreenControl());
      map.addControl(new mapboxgl.NavigationControl());

      map.on('load', function () {
        addVectorTiles(
          map,
          'area',
          stateSchema,
          operationalStates[stateSchema].cogTileServ
        );
      });

      // this array is connected to the addVectorTiles function. if a new tile layer is added or removed
      // alter the below array to reflect the new layers for the 'area' map
      setToggleableLayers(
        createLayerToggle(
          [
            '3DEP Elevation',
            'Counties',
            'States',
            'NHD Flowlines',
            'Tide Stations',
            'Model Elevation'
          ],
          map
        )
      );

      return () => map.remove();

      // Update to stateSchema view
    } else {
      const map = new mapboxgl.Map({
        container: areaMap.current,
        // style: 'mapbox://styles/mapbox/streets-v11',
        style: 'mapbox://styles/mapbox/' + mapStyle,
        center: [
          operationalStates[stateSchema].mapInfo.lng,
          operationalStates[stateSchema].mapInfo.lat
        ],
        zoom: operationalStates[stateSchema].mapInfo.zoom
      });

      map.addControl(new mapboxgl.FullscreenControl());
      map.addControl(new mapboxgl.NavigationControl());

      map.on('load', function () {
        addVectorTiles(
          map,
          'area',
          stateSchema,
          operationalStates[stateSchema].cogTileServ
        );
        hybridLayers.forEach((layer) => {
          if (layer) {
            map.addSource(layer.name, layer.sourceInfo);
            map.addLayer(layer.layerInfo);
          }
        });
      });

      // this array is connected to the addVectorTiles function. if a new tile layer is added or removed
      // alter the below array to reflect the new layers for the 'area' map
      setToggleableLayers(
        createLayerToggle(
          [
            '3DEP Elevation',
            'Counties',
            'States',
            'NHD Flowlines',
            'Tide Stations',
            'Model Elevation'
          ],
          map
        )
      );

      hybridLayers.forEach((layer) => {
        if (layer) {
          map.on('click', layer.layerInfo.id, function (e) {
            new mapboxgl.Popup()
              .setLngLat(e.lngLat)
              .setHTML(mapPopUpTable(e.features[0]))
              .addTo(map);
            handleLocationIDsChange(e.features[0].properties);
          });

          map.on('mouseenter', layer.layerInfo.id, () => {
            map.getCanvas().style.cursor = 'pointer';
          });

          // Change it back to a pointer when it leaves.
          map.on('mouseleave', layer.layerInfo.id, () => {
            map.getCanvas().style.cursor = '';
          });
        }
      });

      // Clean up on unmount
      return () => map.remove();
    }
    // eslint-disable-next-line
  }, [stateSchema, hybridLayers, mapStyle, operationalStates]);

  return { areaMap, handleMapStyleChange, toggleableLayers };
}
